import { useEffect, useMemo, useRef, useState } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import { Box } from '@mui/material';

import config from '@occc/config';
import { LocationAddressFragment } from '@occc/graphql';
import { StaticMap } from '@occc-app/design-system';

import {
  formatAddressForGeocoding,
  geocodeAddressString,
} from '../../retailer-network-my-stores-v2-page/components/store-map-section.utils';
import { getStoreMapPinIcon } from '../../retailer-network-my-stores-v2-page/components/store-map-pin.icon';

const API_LOADER_CONFIG = {
  id: 'google-map-script',
  googleMapsApiKey: config.google.mapsApiKey || '',
};

const MAP_ZOOM = 15;
const MAP_HEIGHT = 300;

type StoreLocationMapCardProps = {
  address?: LocationAddressFragment | null;
};

export const StoreLocationMapCard = ({ address }: StoreLocationMapCardProps) => {
  const { isLoaded } = useJsApiLoader(API_LOADER_CONFIG);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);
  const [coordinates, setCoordinates] = useState<google.maps.LatLngLiteral | null>(null);

  const pinIcon = useMemo(() => (isLoaded ? getStoreMapPinIcon(false) : null), [isLoaded]);

  // Stable key derived from address fields so the effect only re-runs when the address changes.
  const addressKey = [
    address?.geoLocation?.latitude,
    address?.geoLocation?.longitude,
    address?.street1,
    address?.city,
    address?.state,
    address?.zipCode,
  ].join('|');

  useEffect(() => {
    if (!isLoaded) {
      setCoordinates(null);
      return;
    }

    const geo = address?.geoLocation;
    if (geo) {
      setCoordinates({ lat: geo.latitude, lng: geo.longitude });
      return;
    }

    const addressString = formatAddressForGeocoding(address);
    if (!addressString) {
      setCoordinates(null);
      return;
    }

    geocoderRef.current ??= new google.maps.Geocoder();
    let cancelled = false;
    geocodeAddressString(addressString, geocoderRef.current).then((coords) => {
      if (!cancelled) setCoordinates(coords);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, addressKey]);

  return (
    <Box
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: 2,
        bgcolor: 'background.paper',
        width: '100%',
        height: { xs: MAP_HEIGHT, md: '100%' },
      }}
    >
      <StaticMap
        coordinates={coordinates}
        width='100%'
        height='100%'
        zoom={MAP_ZOOM}
        markerIcon={pinIcon}
      />
    </Box>
  );
};
